import React, { useState, useEffect, useRef } from "react";
import closeIcon from "../../Assets/Icons/close.png";
import DateInput from "../../Components/DateInput";
import Dropdown from "../../Components/Dropdown";
import { logFrontendException } from "../../Utils/Exception"; // Importing the exception logger
import { postData } from "../../ApiServices/ApiService"; // Importing the API call
import Loader from "../../Components/Loader";
import { ORG_LEAVE_REQUEST_ADD } from "../../ApiServices/BaseURL";
import { useTheme } from "../../GlobalContext/GlobalContext";

const LeaveRequestModal = ({ isOpen, onClose, onValueChange }) => {
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [leaveType, setLeaveType] = useState(null);
    const [comment, setComment] = useState("");
    const [empName, setEmpName] = useState(""); // Dynamic employee name
    const [openDropdown, setOpenDropdown] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [startDateError, setStartDateError] = useState('');
    const [endDateError, setEndDateError] = useState('');
    const [leaveTypeError, setLeaveTypeError] = useState('');
    const [loading, setLoading] = useState(false); // For loading state
    const { setIsLoginPopup } = useTheme();
    const dropdownRefs = useRef({}); // Ref for dropdown
    const storedUserInfo = JSON.parse(localStorage.getItem('_userInfo_'));
    const storedUserId = storedUserInfo?.emp_id;
    const pageName = "LeaveRequestModal"; // Page name for logging

    useEffect(() => {
        setEmpName(storedUserInfo?.emp_name);
    }, []);

    const leaveTypeOptions = [
        { label: "Casual", value: "CL" },
        { label: "Sick", value: "SL" },
        { label: "Study", value: "ST" },
        { label: "Board", value: "BO" },
    ];

    const calculateDays = () => {
        if (!startDate || !endDate) return 0;
        const diff = Math.floor(
            (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24) + 1
        );
        return diff > 0 ? diff : 0;
    };

    const handleClose = () => {
        setError('');
        setStartDateError('');
        setEndDateError('');
        setLeaveTypeError('');
        onClose();
    }

    const clearFeilds = () => {
        setError('');
        setStartDateError('');
        setEndDateError('');
        setLeaveTypeError('');
        setStartDate('');
        setEndDate('');
        setComment('');
        setLeaveType(null);
    }

    const handleSubmit = async () => {
        try {
            // Clear previous errors
            setError('');
            setStartDateError('');
            setEndDateError('');
            setLeaveTypeError('');

            // Validate fields
            if (!startDate) {
                setStartDateError("Start date is required.");
                return;
            }

            if (!endDate) {
                setEndDateError("End date is required.");
                return;
            }

            if (calculateDays() === 0) {
                setEndDateError("End date must be greater than start date");
                return;
            }

            if (!leaveType) {
                setLeaveTypeError("Leave type is required.");
                return;
            }

            // Prepare the payload
            const payload = {
                action: 'request_add',
                emp_id_auth: storedUserId,
                start_date: startDate,
                end_date: endDate,
                leave_type: leaveType?.value,
                total_days: calculateDays(),
                comment: comment.trim(),
            };

            setLoading(true); // Show the loader

            // Make the API call (dummy endpoint for now)
            const response = await postData(ORG_LEAVE_REQUEST_ADD, payload);
            const responseData = await response.json();

            // Handle the response
            if (response?.status === 200) {
                if (responseData?.statusCode === 200) {
                    if (responseData?.message.toLowerCase() === 'success') {
                        // Successfully submitted
                        setError('')
                        setSuccess(responseData?.info);
                        clearFeilds();
                        setTimeout(() => {
                            handleClose();
                            onValueChange();
                        }, 3000);
                    } else {
                        setError(responseData?.info || 'Failed to submit leave request.');
                        logFrontendException(storedUserId, pageName, "handleSubmit", responseData?.info || '');
                    }
                } else {
                    setError(responseData?.info || 'Failed to add leave request.');
                    logFrontendException(storedUserId, pageName, "handleSubmit", responseData || '');
                }
            } else if (response?.status === 400 || response?.status === 401 || response?.status === 403) {
                            
                setError("Unauthorized access. Your session may have expired. Please log in again.");
                setIsLoginPopup(true)
                logFrontendException(storedUserId, pageName, "handleSubmit", responseData || '');
                        
            } else {
                setError('Error while adding leave request.');
                logFrontendException(storedUserId, pageName, "handleSubmit", response || '');
            }
        } catch (err) {

            // An error occurred in setting up the request
            setError(`Failed to submit leave request. Please check your network connection and try again.`);
            // Log the error
            logFrontendException(storedUserId, pageName, "handleSubmitCatch", err || '');
        } finally {
            setLoading(false); // Hide the loader
        }
    };

    const resetForm = () => {
        setStartDate("");
        setEndDate("");
        setLeaveType(null);
        setComment("");
        setOpenDropdown(null);
    };

    // Handle clicks outside dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            const isOutside = Object.keys(dropdownRefs.current).every(
                (key) => dropdownRefs.current[key] && !dropdownRefs.current[key].contains(event.target)
            );
            if (isOutside) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
   
    const toggleDropdown = (dropdownName) => {
        setOpenDropdown((prev) => (prev === dropdownName ? null : dropdownName));
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <button className="close-button" onClick={onClose}>
                    <img src={closeIcon} alt="Close" style={{ width: 15, height: 15 }} />
                </button>
                <h2 className="modal-title">Request Leave</h2>
                <hr className="title-divider" />

                {/* Employee Name (Outside form-grid) */}
                <div style={{ marginBottom: '10px', padding: '0px' }}>
                    <label style={{ fontSize: '12px', color: '#767676' }}>Employee Name</label>
                    <div className="form-display" style={{ color: '#060606' }}>
                        {empName}
                    </div>
                </div>

                <div className="form-grid" style={{ columnGap: '35px' }}>
                    {/* Date Inputs */}
                    <div>
                        <label style={{ fontSize: '12px', color: '#343C44',fontFamily:'Roboto' }}>Leave Start Date</label>
                        <DateInput
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            errorMessage={startDateError}
                        />
                    </div>

                    <div>
                        <label style={{ fontSize: '12px', color: '#343C44',fontFamily:'Roboto' }}>Leave End Date</label>
                        <DateInput
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            errorMessage={endDateError}
                        />
                    </div>

                    {/* Leave Type and Number of Days */}
                    <div>
                        <Dropdown
                            ref={(el) => (dropdownRefs.current["typeOptions"] = el)}
                            isOpen={openDropdown === "typeOptions"}
                            toggleDropdown={() => toggleDropdown("typeOptions")}
                            label="Select Leave Type"
                            options={leaveTypeOptions}
                            value={leaveType}
                            onChange={(value)=>setLeaveType(value)}
                            errorMessage={leaveTypeError}
                            disabledSelect={true}
                        />
                    </div>

                    <div style={{display:'flex',flexDirection:'column'}}>
                        <label style={{ fontSize: '12px', color: '#343C44',fontFamily:'Roboto' }}>No. of Days</label>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "11px 10px",
                                fontSize: "14px",
                                border: "1px solid #D6E4EC4D",
                                borderRadius: "4px",
                                cursor: "text",
                                marginTop: "5px",
                                backgroundColor: "rgb(255, 255, 255)",
                            }}
                        >
                            {calculateDays()}
                        </div>
                    </div>
                </div>

                {/* Comment (Outside form-grid) */}
                <div style={{ display: "flex", flexDirection: "column", marginTop: '10px' }}>
                    <label style={{ fontSize: '12px', color: '#343C44', marginBottom: '5px',fontFamily:'Roboto' }}>Comment (Optional)</label>
                    <textarea
                        value={comment}
                        onChange={(e) => {
                            if (e.target.value.length <= 200) setComment(e.target.value);
                        }}
                        placeholder="Enter comment (max 200 characters)"
                        style={{
                            width: "96%",
                            padding: "10px",
                            border: "1px solid #D6E4EC",
                            borderRadius: "5px",
                            fontSize: "14px",
                            minHeight: "80px",
                            resize: "none",
                        }}
                    />
                </div>

                <hr className="divider" />
                <div className="button-container">
                    <button
                        onClick={handleSubmit}
                        className="add-client-button"
                        style={{
                            opacity: loading ? 0.5 : 1,
                            cursor: loading ? 'not-allowed' : 'pointer',
                        }}
                        disabled={loading}
                    >
                        <span style={{ paddingRight: loading ? 10 : 0 }}>
                            <Loader loading={loading} color={'#FFFFFF'} size='small' />
                        </span>
                        Submit Request
                    </button>
                    <button
                        className="cancel-button"
                        onClick={() => {
                            resetForm();
                            onClose();
                        }}
                    >
                        Cancel
                    </button>
                    {success && <div className="list-success-msg" style={{ marginBottom: '1px' }}>{success} </div>}
                    {error && <div className="list-error-msg" style={{ marginBottom: '1px' }}>{error}</div>}
                </div>
            </div>
        </div>
    );
};

export default LeaveRequestModal;
